'use client'

import { useTransition } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { isEcommerceProject } from '@/lib/utils'
import { useProjectStore } from '@/hooks/use-project-store'

export default function StripeConnectModal() {
  const router = useRouter()
  const t = useTranslations('settings-connect')
  const [isPending, startTransition] = useTransition()
  const searchParams = useSearchParams()
  const projectId = searchParams.get('id') || ''
  const { project } = useProjectStore()

  const handleConnect = async () => {
    startTransition(async () => {
      try {
        const isEcommerce = project ? isEcommerceProject(project) : false
        const response = await fetch('/api/stripe/connect', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            refreshUrl: isEcommerce
              ? `/project?id=${projectId}&tab=shopping_admin`
              : undefined,
            returnUrl: isEcommerce
              ? `/project?id=${projectId}&tab=shopping_admin`
              : undefined,
          }),
        })
        const data = await response.json()
        if (data.url) {
          router.push(data.url)
        } else if (data.error) {
          toast.error(data.error)
        } else {
          toast.error('Failed to connect to Stripe')
        }
      } catch (error) {
        console.error('Error connecting to Stripe:', error)
        toast.error('Failed to connect to Stripe')
      }
    })
  }

  return (
    <DialogContent className="sm:max-w-[640px] p-6">
      <DialogHeader>
        <DialogTitle className="sr-only">
          {t('modal.connect_to_stripe')}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-8 pb-6">
        <div className="flex items-center justify-center space-x-10 py-4">
          <img
            src="/logos/heyboss.png"
            alt="HeyBoss"
            className="h-16 w-16 rounded-xl"
          />
          <div className="flex items-center">
            <span className="text-gray-400 mx-3 text-2xl">⇄</span>
          </div>
          <img
            src="/logos/stripe.svg"
            alt="Stripe"
            className="h-16 rounded-xl"
          />
        </div>
        <h2 className="text-3xl font-bold text-center">{t('modal.title')}</h2>
        <p className="text-xl text-gray-400 text-center">
          {t('modal.description')}
        </p>
      </div>
      <div className="flex justify-center pb-10 pt-4">
        <Button
          onClick={handleConnect}
          size="lg"
          disabled={isPending}
          variant="light"
        >
          {isPending ? t('modal.connecting') : t('modal.button')}
        </Button>
      </div>
    </DialogContent>
  )
}
